/**
 * The bell in the header.
 *
 * **It polls**, through `hooks/usePolledValue` — which says why polling and not
 * a socket, and why it stops while the tab is hidden. The endpoint it calls is
 * the smallest in the API precisely because it is the one that repeats.
 *
 * The list itself is fetched only when the popover opens. The badge is the cheap
 * thing that runs forever; the rows are the expensive thing that runs on demand.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { UserNotificationDto } from '@forsys/shared';
import { BadgeIconButton } from '../components/BadgeIconButton';
import { Timestamp } from '../components/Timestamp';
import { UnreadDot } from '../components/UnreadDot';
import { usePolledValue } from '../hooks/usePolledValue';
import { listInbox, markAllRead, markRead, unreadCount } from '../api/inbox';

/** How many rows the popover shows before sending the reader to the page. */
const PREVIEW_LIMIT = 8;

export function NotificationBell() {
  const { t } = useTranslation(['inbox', 'common']);
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [count, setCount] = usePolledValue(async () => (await unreadCount()).unreadCount, 0);
  const [items, setItems] = useState<UserNotificationDto[]>([]);
  const [loading, setLoading] = useState(false);

  /** Whether this component is still mounted, for the popover's own requests. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function open(event: React.MouseEvent<HTMLElement>): Promise<void> {
    setAnchor(event.currentTarget);
    setLoading(true);
    try {
      const response = await listInbox({ limit: PREVIEW_LIMIT });
      if (!alive.current) return;
      setItems(response.notifications);
      setCount(response.unreadCount);
    } finally {
      if (alive.current) setLoading(false);
    }
  }

  async function openOne(notification: UserNotificationDto): Promise<void> {
    setAnchor(null);
    if (notification.readAt === null) {
      const { unreadCount: value } = await markRead(notification.id);
      setCount(value);
    }
    navigate('/inbox');
  }

  async function readAll(): Promise<void> {
    await markAllRead();
    setCount(0);
    setItems((current) => current.map((one) => ({ ...one, readAt: new Date().toISOString() })));
  }

  return (
    <>
      <BadgeIconButton
        label={t('inbox:bell')}
        icon={<NotificationsNoneIcon />}
        count={count}
        onClick={(event) => void open(event)}
      />

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: 320, sm: 400 }, maxWidth: '100vw' } } }}
      >
        <Stack
          direction="row"
          sx={{ px: 2, py: 1.25, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="subtitle2">{t('inbox:title')}</Typography>
          {count > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={() => void readAll()}>
              {t('inbox:markAllRead')}
            </Button>
          )}
        </Stack>
        <Divider />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('inbox:noneDescription')}
            </Typography>
          </Box>
        )}

        {!loading && items.length > 0 && (
          <List disablePadding>
            {items.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => void openOne(notification)}
                sx={{ alignItems: 'flex-start', gap: 1, py: 1.25 }}
              >
                <UnreadDot unread={notification.readAt === null} sx={{ mt: 0.75 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {notification.subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <Timestamp value={notification.createdAt} />
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              setAnchor(null);
              navigate('/inbox');
            }}
          >
            {t('inbox:viewAll')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
